import { ref } from 'vue'
import { isTauriRuntime } from '~/lib/platform/runtime'

// Interface zoom (⌘+ / ⌘- / ⌘0). In the app it's the webview's own page zoom — the same as a
// browser's, so layout, hit-testing and scroll math all stay exact; in a plain browser tab the
// browser's zoom is the user's, and CSS `zoom` on <html> stands in for it. Remembered across starts.

const STORAGE_KEY = 'appZoom'
const STEPS = [0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2]

export const zoomLevel = ref(1)

function read(): number {
  try {
    const value = Number(localStorage.getItem(STORAGE_KEY))
    return STEPS.includes(value) ? value : 1
  } catch {
    return 1
  }
}

async function apply(level: number) {
  zoomLevel.value = level
  try {
    localStorage.setItem(STORAGE_KEY, String(level))
  } catch {
    // not persisted — still applied for this session
  }
  if (isTauriRuntime()) {
    try {
      const { getCurrentWebview } = await import('@tauri-apps/api/webview')
      await getCurrentWebview().setZoom(level)
      return
    } catch (e) {
      console.warn('[zoom] webview zoom unavailable, falling back to CSS zoom', e)
    }
  }
  document.documentElement.style.zoom = level === 1 ? '' : String(level)
}

export function zoomIn() {
  const next = STEPS.find((s) => s > zoomLevel.value + 0.001)
  if (next) void apply(next)
}

export function zoomOut() {
  const next = [...STEPS].reverse().find((s) => s < zoomLevel.value - 0.001)
  if (next) void apply(next)
}

export function resetZoom() {
  void apply(1)
}

/** Restores the saved zoom at startup. */
export function restoreZoom() {
  const level = read()
  if (level !== 1) void apply(level)
  else zoomLevel.value = 1
}
